import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import { Link, useHistory } from "react-router-dom";
import type { Server } from "revolt.js";
import { INotificationChecker } from "revolt.js/dist/util/Unreads";
import styled, { css } from "styled-components";
import { useTrigger } from "../../../../../lib/context";
import { DraggableProps } from "../../../../common";
import { Avatar } from "../../../atoms";
import { Tooltip } from "../../../atoms/indicators/Tooltip";
import { Unreads } from "../../../atoms/indicators/Unreads";
import { Swoosh } from "./Swoosh";

export const ItemContainer = styled.div<{ head?: boolean }>`
    width: 56px;
    padding-left: 7px;
    padding-right: 7px;
    padding-bottom: 6px;

    cursor: pointer;

    ${(props) =>
        props.head &&
        css`
            padding-top: 6px;
        `}
`;

const SwooshWrapper = styled.div`
    position: absolute;
    left: -7px;
    top: -32px;

    z-index: -1;
`;

export function SwooshOverlay() {
    return (
        <div style={{ position: "relative" }}>
            <SwooshWrapper>
                <Swoosh />
            </SwooshWrapper>
        </div>
    );
}

type ConditionalLinkProps = {
    active: boolean;
    to: string;
    children: ReactNode;
    onClick: () => void;
    isNative: boolean;
};

function ConditionalLink({
    active,
    to,
    children,
    isNative,
    onClick,
}: ConditionalLinkProps) {
    if (active) {
        return <span>{children}</span>;
    }

    if (isNative) {
        return <span onClick={onClick}>{children}</span>;
    }

    return <Link to={to}>{children}</Link>;
}

const Inner = observer(({ item, permit, active, isNative }: InnerProps) => {
    const Trigger = useTrigger();
    const history = useHistory();
    const unread = !!item.isUnread(permit);
    const count = item.getMentions(permit).length;

    const onSwitchServer = (id: string) => history.replace(`/server/${id}`);

    return (
        <Tooltip content={item.name} div right>
            <Trigger id="Menu" data={{ server: item._id, unread }}>
                <ConditionalLink
                    to={`/server/${item._id}`}
                    active={active}
                    isNative={isNative}
                    onClick={() => onSwitchServer(item._id)}>
                    <Avatar
                        size={42}
                        interactive
                        fallback={item.name}
                        holepunch={(unread || count > 0) && "top-right"}
                        overlay={<Unreads unread={unread} count={count} />}
                        src={item.generateIconURL({ max_side: 256 }, false)}
                    />
                </ConditionalLink>
            </Trigger>
        </Tooltip>
    );
});

export type InnerProps = {
    item: Server;
    permit: INotificationChecker;
    active: boolean;
    isNative?: boolean;
};

type Props = DraggableProps<Server> & InnerProps;

export function Item({
    provided,
    isDragging,
    active,
    isNative,
    ...innerProps
}: Props) {
    return (
        <ItemContainer
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
            style={provided.draggableProps.style}>
            {active && <SwooshOverlay />}
            <Inner {...innerProps} active={active} isNative={isNative} />
        </ItemContainer>
    );
}
